import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  FileQuestion,
  UserPlus,
  Trash2,
  Eye,
  RotateCcw,
  Archive,
  RotateCw,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function MedicalUnmatchedPage() {
  const { token } = useAuth();
  const [files, setFiles] = useState([]);
  const [notHiredFiles, setNotHiredFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [persons, setPersons] = useState([]);
  const [tab, setTab] = useState("pending");

  // Assignment state
  const [assigningId, setAssigningId] = useState(null);
  const [assignPersonId, setAssignPersonId] = useState("");
  const [assignFormDate, setAssignFormDate] = useState("");
  const [assignExpDate, setAssignExpDate] = useState("");

  // Thumbnails — loaded via blob URL like cert system
  const [thumbnails, setThumbnails] = useState({});
  const [loadingThumbnails, setLoadingThumbnails] = useState({});

  // Hover preview + rotation
  const [hoveredFileId, setHoveredFileId] = useState(null);
  const [imageRotation, setImageRotation] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const fetchFiles = async () => {
    try {
      const [pendingRes, notHiredRes, personsRes] = await Promise.all([
        axios.get(`${API}/medical/unmatched-files?status=pending`, { headers }),
        axios.get(`${API}/medical/unmatched-files?status=not_hired`, { headers }),
        axios.get(`${API}/medical/persons/all-names`, { headers }),
      ]);
      setFiles(pendingRes.data);
      setNotHiredFiles(notHiredRes.data);
      setPersons(personsRes.data);
    } catch {
      toast.error("Failed to load unmatched files");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [token]);

  // Load thumbnails via blob URL — same approach as cert system
  const loadThumbnail = async (fileId) => {
    if (thumbnails[fileId] || loadingThumbnails[fileId]) return;
    setLoadingThumbnails((prev) => ({ ...prev, [fileId]: true }));
    try {
      const response = await axios.get(
        `${API}/medical/unmatched-files/${fileId}/thumbnail`,
        { headers, responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      setThumbnails((prev) => ({ ...prev, [fileId]: url }));
    } catch {
      setThumbnails((prev) => ({ ...prev, [fileId]: "error" }));
    } finally {
      setLoadingThumbnails((prev) => ({ ...prev, [fileId]: false }));
    }
  };

  // Auto-load thumbnails for visible files
  const currentFiles = tab === "pending" ? files : notHiredFiles;
  useEffect(() => {
    currentFiles.forEach((f) => {
      if (!thumbnails[f.id] && !loadingThumbnails[f.id]) {
        loadThumbnail(f.id);
      }
    });
  }, [currentFiles]);

  const handleViewUnmatched = async (fileId) => {
    try {
      const response = await axios.get(
        `${API}/medical/unmatched-files/${fileId}/view`,
        { headers, responseType: "blob", timeout: 60000 }
      );
      const contentType = response.headers["content-type"] || "application/pdf";
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error("Failed to view file");
    }
  };

  const handleAssign = async (fileId) => {
    if (!assignPersonId) {
      toast.error("Select a person");
      return;
    }
    try {
      const res = await axios.post(
        `${API}/medical/unmatched-files/${fileId}/assign`,
        {
          person_id: assignPersonId,
          form_date: assignFormDate || null,
          expiration_date: assignExpDate || null,
        },
        { headers }
      );
      toast.success(res.data.message);
      setAssigningId(null);
      setAssignPersonId("");
      fetchFiles();
    } catch {
      toast.error("Assignment failed");
    }
  };

  const handleNotHired = async (fileId) => {
    try {
      await axios.put(`${API}/medical/unmatched-files/${fileId}/not-hired`, {}, { headers });
      toast.success("Moved to Not Hired");
      fetchFiles();
    } catch {
      toast.error("Failed");
    }
  };

  const handleRestore = async (fileId) => {
    try {
      await axios.put(`${API}/medical/unmatched-files/${fileId}/restore`, {}, { headers });
      toast.success("Restored");
      fetchFiles();
    } catch {
      toast.error("Failed");
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm("Delete this file permanently?")) return;
    try {
      await axios.delete(`${API}/medical/unmatched-files/${fileId}`, { headers });
      toast.success("Deleted");
      fetchFiles();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="medical-unmatched">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
          Medical — Unmatched Files
        </h1>
        <p className="text-slate-500 mt-1">
          Files that couldn't be auto-matched to a person
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={tab === "pending" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("pending")}
          className={tab === "pending" ? "bg-teal-700 hover:bg-teal-800" : ""}
          data-testid="medical-unmatched-tab-pending"
        >
          <FileQuestion className="h-4 w-4 mr-1" />
          Pending ({files.length})
        </Button>
        <Button
          variant={tab === "not_hired" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("not_hired")}
          className={tab === "not_hired" ? "bg-teal-700 hover:bg-teal-800" : ""}
          data-testid="medical-unmatched-tab-not-hired"
        >
          <Archive className="h-4 w-4 mr-1" />
          Not Hired ({notHiredFiles.length})
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
        </div>
      ) : currentFiles.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-slate-200">
          <FileQuestion className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">
            No {tab === "pending" ? "unmatched" : "not hired"} files
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
          {currentFiles.map((file) => (
            <div
              key={file.id}
              className="px-6 py-4"
              data-testid={`medical-unmatched-file-${file.id}`}
            >
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Left: Thumbnail with hover zoom */}
                <div className="flex gap-4 flex-1 min-w-0">
                  <div
                    className="relative flex-shrink-0 w-40 h-52"
                    onMouseEnter={() => setHoveredFileId(file.id)}
                    onMouseLeave={() => setHoveredFileId(null)}
                  >
                    <div className="w-full h-full bg-slate-100 rounded-lg border border-slate-200 overflow-hidden">
                      {thumbnails[file.id] && thumbnails[file.id] !== "error" ? (
                        <img
                          src={thumbnails[file.id]}
                          alt="Form preview"
                          className="w-full h-full object-contain cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => handleViewUnmatched(file.id)}
                          data-testid={`medical-thumbnail-${file.id}`}
                        />
                      ) : loadingThumbnails[file.id] ? (
                        <div className="w-full h-full flex items-center justify-center">
                          <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />
                        </div>
                      ) : (
                        <button
                          className="w-full h-full flex flex-col items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
                          onClick={() => loadThumbnail(file.id)}
                        >
                          <Eye className="h-6 w-6 mb-1" />
                          <span className="text-xs">Load Preview</span>
                        </button>
                      )}
                    </div>

                    {/* Large hover preview */}
                    {hoveredFileId === file.id &&
                      thumbnails[file.id] &&
                      thumbnails[file.id] !== "error" && (
                        <div className="absolute left-full top-0 ml-3 z-50">
                          <div
                            className="bg-white rounded-xl shadow-2xl border border-slate-200 p-3 ring-1 ring-black/5"
                            style={{ width: "550px" }}
                          >
                            <img
                              src={thumbnails[file.id]}
                              alt="Enlarged preview"
                              className="w-full object-contain rounded-lg transition-transform duration-200"
                              style={{
                                maxHeight: "700px",
                                transform: `rotate(${imageRotation[file.id] || 0}deg)`,
                              }}
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setImageRotation((prev) => ({
                                  ...prev,
                                  [file.id]:
                                    ((prev[file.id] || 0) + 180) % 360,
                                }));
                              }}
                              className="absolute top-4 right-4 bg-white hover:bg-slate-100 border border-slate-300 rounded-full p-2 shadow-lg transition-colors cursor-pointer"
                              title="Flip 180°"
                              data-testid={`medical-rotate-btn-${file.id}`}
                            >
                              <RotateCw className="h-5 w-5 text-slate-700" />
                            </button>
                          </div>
                        </div>
                      )}
                  </div>

                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-mono text-slate-400 bg-slate-50 px-2 py-0.5 rounded">
                        {file.original_filename}
                        {file.page_index != null
                          ? ` (pg ${file.page_index + 1})`
                          : ""}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-blue-600 hover:text-blue-700"
                        onClick={() => handleViewUnmatched(file.id)}
                        data-testid={`medical-view-unmatched-${file.id}`}
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1" />
                        Open
                      </Button>
                    </div>

                    {file.ocr_text && (
                      <p className="text-xs text-slate-500 mb-2 line-clamp-3 font-mono bg-slate-50 p-2 rounded">
                        {file.ocr_text.slice(0, 200)}
                        {file.ocr_text.length > 200 ? "..." : ""}
                      </p>
                    )}

                    <div className="text-xs text-slate-400 space-y-0.5">
                      <p>
                        Uploaded:{" "}
                        {new Date(file.created_at).toLocaleDateString()}
                        {file.size &&
                          ` · ${(file.size / 1024).toFixed(0)} KB`}
                      </p>
                      {file.form_date && (
                        <p>
                          Detected form date:{" "}
                          {new Date(file.form_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right: Actions */}
                <div className="flex flex-col gap-2 flex-shrink-0 lg:w-48">
                  {tab === "pending" ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="justify-start"
                        onClick={() => {
                          setAssigningId(
                            assigningId === file.id ? null : file.id
                          );
                          setAssignPersonId("");
                          setAssignFormDate(
                            file.form_date
                              ? file.form_date.split("T")[0]
                              : ""
                          );
                          setAssignExpDate(
                            file.expiration_date
                              ? file.expiration_date.split("T")[0]
                              : ""
                          );
                        }}
                        data-testid={`medical-assign-btn-${file.id}`}
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Assign to Person
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="justify-start"
                        onClick={() => handleNotHired(file.id)}
                      >
                        <Archive className="h-4 w-4 mr-2" />
                        Not Hired
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="justify-start text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleDelete(file.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="justify-start"
                        onClick={() => handleRestore(file.id)}
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Restore to Queue
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="justify-start text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleDelete(file.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Assignment panel */}
              {assigningId === file.id && (
                <div className="mt-3 p-4 bg-slate-50 rounded-lg border space-y-3">
                  <select
                    value={assignPersonId}
                    onChange={(e) => setAssignPersonId(e.target.value)}
                    className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                    data-testid="medical-unmatched-assign-person"
                  >
                    <option value="">Select person...</option>
                    {persons.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.person_type} — {p.division})
                      </option>
                    ))}
                  </select>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 font-medium">
                        Form Date
                      </label>
                      <input
                        type="date"
                        value={assignFormDate}
                        onChange={(e) => {
                          setAssignFormDate(e.target.value);
                          if (e.target.value) {
                            const dt = new Date(e.target.value);
                            dt.setFullYear(dt.getFullYear() + 1);
                            setAssignExpDate(dt.toISOString().split("T")[0]);
                          }
                        }}
                        className="w-full h-8 rounded-md border border-slate-200 px-2 text-sm mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 font-medium">
                        Expiration
                      </label>
                      <input
                        type="date"
                        value={assignExpDate}
                        onChange={(e) => setAssignExpDate(e.target.value)}
                        className="w-full h-8 rounded-md border border-slate-200 px-2 text-sm mt-1"
                      />
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleAssign(file.id)}
                    className="bg-teal-700 hover:bg-teal-800"
                    data-testid="medical-unmatched-assign-submit"
                  >
                    Assign to Person
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
